// The Namat scale per pillar (#124): the questionnaire's answers grouped by
// their question's category (the six lifestyle pillars), each pillar scored as
// sum(score) / sum(max) of its answered questions. The overall scale stays
// assessNamat's; this only splits the questionnaire part.
//
// Pure and import-free on purpose: namat-mobile keeps a byte-for-byte copy at
// the same path. Change both together.

export const PILLARS = ['Nutrition', 'Exercises', 'Sleep', 'Stress', 'Relations', 'Addictions'] as const;
export type Pillar = (typeof PILLARS)[number];

/** Used when a question's highest option score is unknown (as assessNamat does). */
export const DEFAULT_QUESTION_MAX = 3;

export function isPillar(value: unknown): value is Pillar {
  return typeof value === 'string' && (PILLARS as readonly string[]).includes(value);
}

/** A question's highest option score: non-numeric scores count as 0, no options give 0. */
export function maxOptionScore(options: { score?: unknown }[] | null | undefined): number {
  return Math.max(0, ...(options || []).map((o) => Number(o.score) || 0));
}

export interface PillarAnswer {
  questionId: string;
  score: unknown;
  /** Saved on the answer since the questionnaire started storing it. */
  category?: string | null;
}

export interface PillarLookup {
  /** The questionnaire's category per question, for answers saved without one. */
  categoryByQuestion?: Record<string, string | null | undefined>;
  /** Highest option score per question; missing = DEFAULT_QUESTION_MAX. */
  maxScoreByQuestion?: Record<string, number>;
}

export interface PillarScore {
  pillar: Pillar;
  score: number;
  max: number;
  /** Rounded 0-100. */
  percent: number;
  /** How many answered questions fed this pillar. */
  answered: number;
}

/** The answer's pillar: its own category first, then the questionnaire's; null when neither is a pillar. */
export function pillarOf(answer: PillarAnswer, lookup: PillarLookup = {}): Pillar | null {
  if (isPillar(answer.category)) return answer.category;
  const fromQuestionnaire = lookup.categoryByQuestion?.[answer.questionId];
  return isPillar(fromQuestionnaire) ? fromQuestionnaire : null;
}

/**
 * Answers grouped by pillar, in PILLARS order, each group in answer order.
 * Pillars without answers and answers without a pillar are left out.
 */
export function groupByPillar<T extends PillarAnswer>(
  answers: T[],
  lookup: PillarLookup = {}
): { pillar: Pillar; answers: T[] }[] {
  const groups = new Map<Pillar, T[]>();
  for (const answer of answers) {
    const pillar = pillarOf(answer, lookup);
    if (!pillar) continue;
    const list = groups.get(pillar);
    if (list) list.push(answer);
    else groups.set(pillar, [answer]);
  }
  return PILLARS.filter((p) => groups.has(p)).map((pillar) => ({ pillar, answers: groups.get(pillar)! }));
}

/**
 * One score per pillar that has answers, in PILLARS order. A pillar whose
 * questions all have max 0 is left out (nothing to score against).
 */
export function pillarScores(answers: PillarAnswer[], lookup: PillarLookup = {}): PillarScore[] {
  const out: PillarScore[] = [];
  for (const group of groupByPillar(answers, lookup)) {
    let score = 0;
    let max = 0;
    for (const a of group.answers) {
      score += Number(a.score) || 0;
      max += lookup.maxScoreByQuestion?.[a.questionId] ?? DEFAULT_QUESTION_MAX;
    }
    if (max <= 0) continue;
    const percent = Math.max(0, Math.min(100, Math.round((score / max) * 100)));
    out.push({ pillar: group.pillar, score, max, percent, answered: group.answers.length });
  }
  return out;
}
